/**
 * 認証フック
 *
 * 管理画面用の認証状態管理
 */

import type { PublicUser } from "@repo/types";
import { useCallback, useEffect, useState } from "react";
import { API_URL, apiClient } from "@/lib/api-client";

// ============================================
// 型定義
// ============================================

interface AuthState {
  isAuthenticated: boolean;
  user: PublicUser | null;
  isLoading: boolean;
  error: string | null;
}

// ============================================
// フック
// ============================================

export function useAuth() {
  const [state, setState] = useState<AuthState>({
    isAuthenticated: false,
    user: null,
    isLoading: true,
    error: null,
  });

  // 認証状態を取得
  const fetchAuthStatus = useCallback(async () => {
    try {
      const response = await apiClient.api.v1.auth.me.$get();

      if (response.ok) {
        const data = await response.json();
        setState({
          isAuthenticated: true,
          user: data.user as PublicUser,
          isLoading: false,
          error: null,
        });
      } else {
        setState({
          isAuthenticated: false,
          user: null,
          isLoading: false,
          error: null,
        });
      }
    } catch (_error) {
      setState({
        isAuthenticated: false,
        user: null,
        isLoading: false,
        error: "認証状態の確認に失敗しました",
      });
    }
  }, []);

  // Google ログイン
  const login = useCallback(() => {
    const currentUrl = window.location.href;
    const redirectUrl = `${API_URL}/api/v1/auth/google?redirect=${encodeURIComponent(currentUrl)}`;
    window.location.href = redirectUrl;
  }, []);

  // ログアウト
  const logout = useCallback(async () => {
    try {
      await apiClient.api.v1.auth.logout.$post();
      setState({
        isAuthenticated: false,
        user: null,
        isLoading: false,
        error: null,
      });
    } catch (error) {
      console.error("Logout error:", error);
    }
  }, []);

  // 初回マウント時に認証状態を取得
  useEffect(() => {
    fetchAuthStatus();
  }, [fetchAuthStatus]);

  return {
    ...state,
    login,
    logout,
    refresh: fetchAuthStatus,
  };
}
